import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, relTime, type RouteAction } from '../lib/api'
import { SearchableSelect } from '../components/SearchableSelect'

/**
 * Inbound routes. Match received mail (to/from/subject) and act on it: forward to an address,
 * POST to a webhook, keep it, or stop further routes. Evaluated in priority order server-side.
 */
export function InboundRoutes() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['routes'], queryFn: api.routes.list })
  const rows = data?.data ?? []

  const [field, setField] = useState('to')
  const [op, setOp] = useState('catch_all')
  const [value, setValue] = useState('')
  const [actionType, setActionType] = useState('forward')
  const [target, setTarget] = useState('')
  const [priority, setPriority] = useState(100)
  const [description, setDescription] = useState('')
  const [error, setError] = useState<ApiError | null>(null)

  const create = useMutation({
    mutationFn: () => {
      const action: RouteAction = actionType === 'forward' ? { type: 'forward', to: target }
        : actionType === 'webhook' ? { type: 'webhook', url: target }
        : { type: actionType }
      return api.routes.create({
        condition: { field, op, value: op === 'catch_all' ? null : value },
        actions: [action],
        priority,
        description: description || undefined,
      })
    },
    onSuccess: () => { setValue(''); setTarget(''); setDescription(''); setError(null); qc.invalidateQueries({ queryKey: ['routes'] }) },
    onError: (e) => setError(e as ApiError),
  })
  const toggle = useMutation({
    mutationFn: (r: { id: string; active: boolean }) => r.active ? api.routes.disable(r.id) : api.routes.enable(r.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routes'] }),
  })
  const remove = useMutation({
    mutationFn: (id: string) => api.routes.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routes'] }),
  })

  const needsTarget = actionType === 'forward' || actionType === 'webhook'

  return (
    <div>
      <h1>Inbound routes</h1>
      <p className="lede">
        When mail arrives at a domain with inbound enabled, routes decide what happens — forward
        it, POST it to a webhook, keep it, or stop. Lower priority runs first.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <h2 style={{ marginTop: 0 }}>New route</h2>
        <form onSubmit={e => { e.preventDefault(); create.mutate() }}>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>When</label>
              <SearchableSelect value={field} onChange={setField} options={[
                { value: 'to', label: 'To' }, { value: 'from', label: 'From' },
                { value: 'subject', label: 'Subject' }, { value: 'any', label: 'Anything' },
              ]} />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Operator</label>
              <SearchableSelect value={op} onChange={setOp} options={[
                { value: 'catch_all', label: 'catch all' }, { value: 'equals', label: 'equals' },
                { value: 'contains', label: 'contains' }, { value: 'starts_with', label: 'starts with' },
                { value: 'ends_with', label: 'ends with' }, { value: 'domain_is', label: 'domain is' },
              ]} />
            </div>
            {op !== 'catch_all' && (
              <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 160 }}>
                <label>Value</label>
                <input value={value} onChange={e => setValue(e.target.value)} placeholder="support@ / example.com / invoice" />
              </div>
            )}
          </div>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginTop: 12 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Then</label>
              <SearchableSelect value={actionType} onChange={setActionType} options={[
                { value: 'forward', label: 'Forward to' }, { value: 'webhook', label: 'POST to webhook' },
                { value: 'store', label: 'Store only' }, { value: 'stop', label: 'Stop' },
              ]} />
            </div>
            {needsTarget && (
              <div className="field" style={{ marginBottom: 0, flex: 1, minWidth: 220 }}>
                <label>{actionType === 'forward' ? 'Destination address' : 'Webhook URL'}</label>
                <input value={target} onChange={e => setTarget(e.target.value)}
                       placeholder={actionType === 'forward' ? 'ops@yourcompany.com' : 'https://api.you.com/hook'} />
              </div>
            )}
            <div className="field" style={{ marginBottom: 0, width: 100 }}>
              <label>Priority</label>
              <input type="number" value={priority} onChange={e => setPriority(Number(e.target.value))} />
            </div>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label>Description <span className="muted">(optional)</span></label>
            <input value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          {error && <div className="alert error" style={{ marginBottom: 12 }}>{error.message}</div>}
          <button type="submit" disabled={create.isPending || (needsTarget && !target)}>
            {create.isPending ? 'Creating…' : 'Create route'}
          </button>
        </form>
      </div>

      {isLoading && <div className="skeleton" style={{ height: 140 }} />}
      {!isLoading && rows.length === 0 && (
        <div className="empty"><div className="big">↳</div><p><strong>No routes</strong></p><p>Add one to act on incoming mail.</p></div>
      )}

      {rows.length > 0 && (
        <div className="card flush">
          <table className="data">
            <thead><tr><th>Priority</th><th>When</th><th>Then</th><th>Matches</th><th /></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={{ opacity: r.active ? 1 : 0.55 }}>
                  <td className="mono">{r.priority}</td>
                  <td className="small">
                    {r.condition?.op === 'catch_all'
                      ? <span className="mono">any mail</span>
                      : <span className="mono">{r.condition?.field} {r.condition?.op} “{r.condition?.value}”</span>}
                    {r.description && <div className="muted">{r.description}</div>}
                    {!r.active && <span className="badge failed">disabled</span>}
                  </td>
                  <td className="small">
                    {r.actions.map((a, i) => (
                      <span key={i} className="badge info" style={{ marginRight: 4 }}>
                        {a.type}{a.to ? ` → ${a.to}` : ''}{a.url ? ' → webhook' : ''}
                      </span>
                    ))}
                  </td>
                  <td className="small muted">{r.match_count}{r.last_matched_at ? ` · ${relTime(r.last_matched_at)}` : ''}</td>
                  <td className="right">
                    <button className="ghost small" onClick={() => toggle.mutate({ id: r.id, active: r.active })}>
                      {r.active ? 'Disable' : 'Enable'}
                    </button>
                    <button className="ghost small danger" onClick={() => { if (confirm('Delete this route?')) remove.mutate(r.id) }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
